import { useEffect, useState } from "react";
import api, { fileUrl } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { InvoiceImages } from "./InvoiceImages";
import { ProductImg } from "./JubelioPicker";
import { fmtCur, fmtIDR, fmtNum, inp, toastErr } from "./util";

function Photo({ g }) {
    const [open, setOpen] = useState(false);
    const src = g.matched ? (g.catalog_image && fileUrl(g.catalog_image)) || g.thumbnail : null;
    const img = <ProductImg key={`${g.catalog_image}|${g.thumbnail}`} p={g} className="w-14 h-14" />;
    if (!src) return img;
    return (
        <>
            <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} className="shrink-0 cursor-zoom-in" data-testid={`history-photo-${g.key}`} aria-label="Lihat foto">{img}</button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-auto p-3 sm:p-5" data-testid="history-photo-dialog">
                    <DialogHeader><DialogTitle className="text-sm pr-6">{g.product_name} · {g.jubelio_item_code}</DialogTitle></DialogHeader>
                    <img src={src} alt={g.product_name} className="w-full h-auto" style={{ touchAction: "pinch-zoom" }} data-testid="history-photo-full" />
                </DialogContent>
            </Dialog>
        </>
    );
}

function Group({ g, onOpen }) {
    const [open, setOpen] = useState(false);
    const l = g.latest, lo = g.lowest_base_cost_idr;
    const ssku = g.purchases.find((p) => p.supplier_sku)?.supplier_sku;
    return (
        <div className="border border-mist rounded-sm bg-white p-3 space-y-2" data-testid={`history-group-${g.key}`}>
            <div className="flex gap-3 items-start">
                <Photo g={g} />
                <button type="button" className="min-w-0 flex-1 text-left text-xs space-y-0.5" onClick={() => setOpen(!open)} data-testid={`history-toggle-${g.key}`}>
                    <div className="text-sm font-medium text-ink leading-snug">{g.product_name || "(no name)"}</div>
                    {g.matched ? <div className="text-neutral-500 break-all">Jubelio SKU {g.jubelio_item_code}</div> : (
                        <>
                            {ssku && <div className="text-neutral-600 break-all" data-testid={`history-supplier-sku-${g.key}`}>Supplier SKU: <span className="font-mono font-semibold">{ssku}</span></div>}
                            <div className="text-rose-700">Unmatched</div>
                        </>
                    )}
                    <div className="pt-1.5" data-testid={`history-latest-${g.key}`}>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-400">Latest · {l.invoice_date}</div>
                        <div className="text-base font-semibold text-ink tabular-nums">{fmtCur(l.currency, l.unit_price)} <span className="text-neutral-400 font-normal">/</span> {fmtIDR(l.base_cost_idr)}</div>
                    </div>
                    <div className="text-neutral-500 tabular-nums" data-testid={`history-lowest-${g.key}`}>Lowest: {fmtCur(lo.currency, lo.unit_price)} · {fmtIDR(lo.base_cost_idr)} <span className="text-neutral-400">({lo.supplier_name}, {lo.invoice_date})</span></div>
                </button>
            </div>
            <div className="text-xs space-y-1 text-neutral-500 border-t border-mist/60 pt-2">
                {g.suppliers.map((s) => <div key={s.supplier_name} data-testid={`history-supplier-${g.key}-${s.supplier_name}`}>{s.supplier_name} — {fmtCur(s.currency, s.latest_unit_price)} <span className="text-neutral-400">({s.latest_date})</span></div>)}
            </div>
            {open && (
                <div className="overflow-x-auto border-t border-mist pt-2">
                    <table className="w-full text-xs min-w-[560px]" data-testid={`history-table-${g.key}`}>
                        <thead className="text-neutral-400 text-[10px]"><tr><th className="text-left py-1">Date</th><th className="text-left">Supplier</th><th className="text-left">Supplier SKU</th><th className="text-right">Qty</th><th className="text-right">Unit Price</th><th className="text-right">Rate</th><th className="text-right">Cost / pc</th><th /></tr></thead>
                        <tbody>
                            {g.purchases.map((p, n) => (
                                <tr key={`${p.invoice_id}-${n}`} className="border-t border-mist/60">
                                    <td className="py-1"><button type="button" className="underline" onClick={() => onOpen(p.invoice_id)} data-testid={`history-open-invoice-${p.invoice_id}`}>{p.invoice_date}</button></td>
                                    <td>{p.supplier_name}{p.invoice_number && <span className="text-neutral-400"> #{p.invoice_number}</span>}</td><td className="font-mono">{p.supplier_sku}</td>
                                    <td className="text-right">{fmtNum(p.qty)}</td><td className="text-right">{fmtCur(p.currency, p.unit_price)}</td><td className="text-right">{fmtNum(p.exchange_rate)}</td><td className="text-right">{fmtIDR(p.base_cost_idr)}</td>
                                    <td className="pl-2"><InvoiceImages fileIds={p.file_ids} label="Invoice" testid={`history-original-${p.invoice_id}-${n}`} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default function PriceHistory({ onOpen }) {
    const [q, setQ] = useState("");
    const [rows, setRows] = useState(null);
    useEffect(() => {
        const t = setTimeout(() => { api.get("/invoices/history", { params: { q } }).then(({ data }) => setRows(data)).catch((e) => toastErr(e, "Failed to load history")); }, 250);
        return () => clearTimeout(t);
    }, [q]);
    return (
        <div className="space-y-3" data-testid="price-history">
            <input className={inp} placeholder="Product, SKU or supplier" value={q} onChange={(e) => setQ(e.target.value)} data-testid="history-search" />
            {rows && rows.length === 0 && <p className="text-sm text-neutral-400">No purchase history found.</p>}
            {(rows || []).map((g) => <Group key={g.key} g={g} onOpen={onOpen} />)}
        </div>
    );
}
